// -------------- import external deendencies ------------
import { useFormik } from "formik";
import { toast } from "react-toastify";

// ----------- import internal dependencies -----------
import FormInput from "../../../../../components/Forms/input/FormInput";
import Button from "../../../../../components/Button";
import { validateEmail } from "../../../../../utils/validators";
import { UserCreation } from "../../type";
import { useStoreWrapper } from "../../../../../store/store";
import useUser from "../../../hooks/useUser";
import { useEffect, useState } from "react";
import { isContinueDisabled } from "./methods";

const toastConfig = {
  position: toast.POSITION.TOP_RIGHT,
  autoClose: 2000,
  toastId: "1",
};

function UserEmail({ cb }: UserCreation) {
  // -------- custom hook initialization ------
  const { registerUser } = useUser();
  const assignments = useStoreWrapper((state) => state.assignment);
  const updateEmail = useStoreWrapper((state) => state.updateEmail);
  const title = useStoreWrapper((state) => state.title);
  const firstName = useStoreWrapper((state) => state.firstName);
  const lastName = useStoreWrapper((state) => state.lastName);
  const email = useStoreWrapper((state) => state.email);
  const user = useStoreWrapper((state) => state.user);
  let username = useStoreWrapper((state) => state.userName);
  // No prefix means no "prefix." at all; MrWebApp_LoginIdentityService does the same.
  const accountUsernamePrefix = user.accountUsernamePrefix;
  username = accountUsernamePrefix
    ? `${accountUsernamePrefix}.${username}`
    : username;

  const [requireEmail, setRequireEmail] = useState(true);

  const { mutateAsync, isLoading } = registerUser;

  const emailArr = ["Guest User", "Incident Entry", "Supervisor"];

  useEffect(() => {
    const containsEmaillArr =
      assignments.filter((assignment) =>
        emailArr.includes(assignment.profile.profileName)
      ).length === 1;

    const onlyEmailArr = assignments.every((assignment) =>
      emailArr.includes(assignment.profile.profileName)
    );

    // Set requireEmail to false only if there's exactly one assignment and it's a in the emailArr
    // Or if all assignments are in the emailArr.
    // Otherwise, set to true.
    const shouldRequireEmail =
      !(containsEmaillArr && assignments.length === 1) && !onlyEmailArr;
    setRequireEmail(shouldRequireEmail);
  }, [assignments]);

  /**
   * Form validation config
   */
  const formik = useFormik({
    initialValues: {
      email,
      checked: false,
    },
    validationSchema: validateEmail(),
    onSubmit: async (values: any) => {
      const { email } = values;

      // ------ check condition for required email --------
      if (requireEmail && !email) {
        toast.error(
          "Please provide an email to create this profile",
          toastConfig
        );
        return;
      }

      let facilities = assignments.map((facility) => {
        let facilityObj: any = { ...facility.profile.permissions };

        facilityObj.facilityId = facility.facility.facilityId;
        facilityObj.profileId = facility.profile.profileId;
        facilityObj.roleId = facility.role.roleId;

        return facilityObj;
      });

      updateEmail(email);

      try {
        const { userId: loggedInUserId } = user;

        const response = await mutateAsync({
          loggedInUserId,
          title,
          firstName,
          lastName,
          username,
          ...(formik.values.email && { email }),
          facilities: [...facilities],
        });

        // Contact id when the API returns one, `true` from an older API that returns none.
        cb(response?.data?.data?.contactId ?? true);
      } catch (error: any) {
        console.log(error);
        toast.error(
          error?.response?.data?.message ?? error?.message,
          toastConfig
        );
      }
    },
  });

  const checkDisabled = () =>
    isContinueDisabled({
      requireEmail,
      email: formik.values.email,
      checked: formik.values.checked,
      isLoading,
    });

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <FormInput
          label="Email Address"
          name="email"
          placeholder="Enter company email address"
          variant="sm"
          type="email"
          value={formik.values.email}
          onChange={(e) => {
            formik.setFieldValue("email", e.target.value);
          }}
          errorMessage={formik.errors.email ? formik.errors.email : null}
        />
        <FormInput name="checked" variant="sm">
          <input
            type="checkbox"
            id="validate"
            className="accent-purple-light"
            checked={formik.values.checked}
            onChange={(e) => {
              formik.setFieldValue("checked", e.target.checked);
            }}
          />
          <label htmlFor="validate" className="text-purple-300">
            Click to confirm that the email entered isn’t a personal email
          </label>
        </FormInput>

        <div className="mt-10 flex items-center justify-end gap-3">
          <Button
            variant="md"
            type="submit"
            disabled={checkDisabled()}
            textContent="Continue"
            addClass="bg-purple-main ml-auto block text-lg text-white-main px-8"
          />
        </div>
      </form>
    </>
  );
}

export default UserEmail;
